import { createContext, useCallback, useEffect, useMemo, useState } from 'react';
import { useLocation } from 'react-router-dom';

import { ThemeType, ThemeModule, IThemeContext } from '@themes/themeTypes';
import { validateModulePathname } from '@themes/utils';

const darkScheme = 'prefers-color-scheme: dark';
const lightScheme = 'prefers-color-scheme: light';

export const THEME_TYPE = {
  LIGHT: 'light' as ThemeType,
  DARK: 'dark' as ThemeType,
};

export const THEME_MODULE = {
  AUTH: 'auth' as ThemeModule,
  ACCOUNT: 'account' as ThemeModule,
  INSIGHTS: 'insights' as ThemeModule,
  RECOMMENDATIONS: 'recommendations',
  UPLOADER: 'uploader' as ThemeModule,
  CREATOR_DASHBOARD: 'dashboard' as ThemeModule,
};

const themeStorageKey = 'color-theme';
const matchClientMedia = (mq: string): boolean => window.matchMedia(`(${mq})`).matches;

const getInitialTheme = (): ThemeType => {
  // If we don't have user prefs in localStorage, load the theme from
  //  the environment prefs (browser || OS).
  if (typeof window !== 'undefined' && window.localStorage) {
    const storedThemePrefs = window.localStorage.getItem(themeStorageKey) as ThemeType;
    const prefersDark = matchClientMedia(darkScheme);
    const prefersLight = matchClientMedia(lightScheme);

    if (storedThemePrefs) {
      return storedThemePrefs;
    }

    if (prefersDark) {
      return THEME_TYPE.DARK;
    }

    // Disable light theme for now
    if (prefersLight) {
      return THEME_TYPE.LIGHT;
    }
  }

  return THEME_TYPE.LIGHT;
};

const ThemeContext = createContext<IThemeContext | null>(null);

const ThemeProvider = ({
  initialTheme,
  children,
}: {
  initialTheme?: ThemeType;
  children?: React.ReactNode;
}) => {
  const { pathname } = useLocation();
  const [theme, setTheme] = useState<ThemeType>(getInitialTheme());
  const [moduleTheme, setModuleTheme] = useState(THEME_MODULE.AUTH);
  const moduleName = useMemo(() => {
    const _pathname = pathname.split('/');
    return validateModulePathname(Object.values(THEME_MODULE) as [], _pathname[1] as ThemeModule);
  }, [pathname]);

  const getModuleNameFromLocation = useCallback(
    (pathname: string) => {
      if (!pathname.length) {
        setModuleTheme(THEME_MODULE.AUTH);
      } else {
        setModuleTheme(moduleName as unknown as ThemeModule);
      }
    },
    [moduleName]
  );

  const saveUserPrefs = (theme: ThemeType) => {
    setTheme(theme);
    localStorage.setItem(themeStorageKey, theme);
  };

  if (initialTheme) {
    saveUserPrefs(initialTheme);
  }

  useEffect(() => {
    if (typeof window != 'undefined') {
      window
        .matchMedia('(prefers-color-scheme: dark)')
        .addEventListener('change', () => setTheme(getInitialTheme));

      return window
        .matchMedia('(prefers-color-scheme: dark)')
        .removeEventListener('change', () => setTheme(getInitialTheme));
    }
  }, []);

  useEffect(() => {
    getModuleNameFromLocation(moduleName);
  }, [moduleName]);

  return (
    <ThemeContext.Provider value={{ theme, setTheme, saveUserPrefs, moduleTheme }}>
      {children}
    </ThemeContext.Provider>
  );
};

export { ThemeContext, ThemeProvider };
